import { FC } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MessageSquareIcon, Loader2Icon } from 'lucide-react';

import { useAuthToken } from '@/hooks/useAuthToken';
import { fetchComments } from '@/api/comments';
import { CommentItem } from './CommentItem';
import { CommentForm } from './CommentForm';

interface CommentsProps {
  petId: number;
  petStatus: PetStatus;
}

const STATUS_FILL: Record<PetStatus, string> = {
  lost: 'fill-lost',
  found: 'fill-found',
};

export const Comments: FC<CommentsProps> = ({ petId, petStatus }) => {
  const { token, isTokenExpired } = useAuthToken();
  const queryKey = `comments-pet-${petId}`;

  const {
    data: comments,
    isLoading,
    isError,
  } = useQuery({
    queryKey: [queryKey],
    queryFn: () => fetchComments(petId),
  });

  const isAuthenticated = token && !isTokenExpired(token);

  return (
    <div className="mt-6 w-full px-4">
      {/* Section header */}
      <div
        className={`${STATUS_FILL[petStatus]} flex items-center gap-2 px-3 py-2`}
        style={{
          border: '3px solid var(--nb-ink)',
          boxShadow: '3px 3px 0 var(--nb-ink)',
          borderRadius: 'var(--nb-radius)',
        }}
      >
        <MessageSquareIcon size={16} />
        <div
          className="text-[10px] font-bold uppercase tracking-[0.08em] opacity-70"
          style={{ fontFamily: '"JetBrains Mono", monospace' }}
        >
          / comentarios
        </div>
        {comments && comments.length > 0 && (
          <span
            className="ml-auto text-[11px] font-bold"
            style={{ fontFamily: '"JetBrains Mono", monospace' }}
          >
            {comments.length}
          </span>
        )}
      </div>

      <div className="pt-3">
        {isAuthenticated ? (
          <CommentForm petId={petId} queryKey={queryKey} />
        ) : (
          <div
            className="px-3 py-3 text-center text-[13px]"
            style={{
              border: '2px solid var(--nb-ink)',
              background: 'var(--nb-paper)',
              borderRadius: 'var(--nb-radius)',
            }}
          >
            <a href="/login" className="font-bold underline decoration-2 underline-offset-2">
              Iniciá sesión
            </a>{' '}
            para dejar un comentario.
          </div>
        )}
      </div>

      {isLoading && (
        <div className="flex justify-center py-8">
          <Loader2Icon className="animate-spin" size={20} />
        </div>
      )}

      {isError && (
        <p
          className="pt-3 text-center text-[12px] font-semibold"
          style={{ color: '#b42020', fontFamily: '"JetBrains Mono", monospace' }}
        >
          Error al cargar los comentarios. Recargá la página.
        </p>
      )}

      {!isLoading && !isError && comments && (
        <div className="flex flex-col gap-2.5 pt-3">
          {comments.length === 0 ? (
            <div
              className="nb-hatch px-3 py-5 text-center text-[13px] font-medium opacity-70"
              style={{
                border: '2px dashed var(--nb-ink)',
                background: 'var(--nb-paper)',
              }}
            >
              Todavía no hay comentarios. Sé el primero.
            </div>
          ) : (
            comments.map((comment) => (
              <CommentItem key={comment.id} comment={comment} petId={petId} queryKey={queryKey} />
            ))
          )}
        </div>
      )}
    </div>
  );
};
